/**
 * CTE Shared React Components
 * Reusable visual components used by CTECalculator and MouldCompensation.
 */

import React, { useEffect, useRef, useState, useMemo, useContext, useCallback, createContext } from 'react';
import * as THREE from 'three';
import {
  jetColor, getDispMagnitude, computeBaseRef,
  getBuildDirectionVector, createBuildArrow,
  loadCadModel,
} from './cte-utils';
import { bindViewerBackground } from '../../utils/theme';

const CadModelContext = createContext(null);

export function CadModelProvider({ children }) {
  const [model, setModel] = useState({
    fileName: null,
    positions: null,
    rawBytes: null,
    isStep: false,
    warning: null,
    error: null,
    loading: false,
  });
  const value = useMemo(() => ({ model, setModel }), [model]);
  return <CadModelContext.Provider value={value}>{children}</CadModelContext.Provider>;
}

export function useCadModelUpload() {
  const ctx = useContext(CadModelContext);
  if (!ctx) throw new Error('CAD model is only available inside the CTE tool');
  const { model, setModel } = ctx;
  const loadFile = useCallback(async (file) => {
    if (!file) return;
    setModel((m) => ({ ...m, loading: true, error: null }));
    try {
      const loaded = await loadCadModel(file);
      setModel({ ...loaded, loading: false, error: null });
    } catch (err) {
      setModel((m) => ({
        ...m,
        loading: false,
        error: 'Failed to parse file: ' + (err && err.message ? err.message : 'unknown error'),
      }));
    }
  }, [setModel]);
  return { model, setModel, loadFile };
}

function layoutPlate(size) {
  const x = Math.max(1e-6, Number(size && size.x) || 200);
  const y = Math.max(1e-6, Number(size && size.y) || 100);
  const z = Math.max(1e-6, Number(size && size.z) || 12);
  const k = 2 / Math.max(x, y, z);
  return { sx: x * k, sy: y * k, sz: z * k };
}

// ─── THREE.JS VIEWER ──────────────────────────────────────────────────────────
export function STLViewer({ positions, strainTensor, maxDisp, showOriginal, orient, defScale = 1 }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current || !positions) return;
    const W = mountRef.current.clientWidth || 700;
    const H = mountRef.current.clientHeight || 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    const unbindBg = bindViewerBackground(renderer, 0x0a0a0f);
    mountRef.current.innerHTML = '';
    mountRef.current.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.001, 100000);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(1, 2, 1.5);
    scene.add(dirLight);

    let minX=Infinity, minY=Infinity, minZ=Infinity, maxX=-Infinity, maxY=-Infinity, maxZ=-Infinity;
    const nv = positions.length / 3;
    for (let i = 0; i < nv; i++) {
      const x=positions[i*3], y=positions[i*3+1], z=positions[i*3+2];
      if (x<minX)minX=x; if (x>maxX)maxX=x;
      if (y<minY)minY=y; if (y>maxY)maxY=y;
      if (z<minZ)minZ=z; if (z>maxZ)maxZ=z;
    }
    const spanX = maxX - minX, spanY = maxY - minY, spanZ = maxZ - minZ;
    const span = Math.max(spanX, spanY, spanZ);
    const centreX = spanX / 2, centreY = spanY / 2, centreZ = spanZ / 2;
    const base = computeBaseRef(positions);
    const bx = base.x, by = base.y, bz = base.z;

    const [e00,e01,e02,e10,e11,e12,e20,e21,e22] = [
      strainTensor[0][0], strainTensor[0][1], strainTensor[0][2],
      strainTensor[1][0], strainTensor[1][1], strainTensor[1][2],
      strainTensor[2][0], strainTensor[2][1], strainTensor[2][2],
    ];

    const maxMag = maxDisp > 0 ? maxDisp : 1;

    const deformedPositions = new Float32Array(positions.length);
    const colors = new Float32Array(nv * 3);
    for (let i = 0; i < nv; i++) {
      const rx = positions[i*3]-bx, ry = positions[i*3+1]-by, rz = positions[i*3+2]-bz;
      const dx = e00*rx + e01*ry + e02*rz;
      const dy = e10*rx + e11*ry + e12*rz;
      const dz = e20*rx + e21*ry + e22*rz;
      deformedPositions[i*3  ] = (positions[i*3]-minX) + dx * defScale;
      deformedPositions[i*3+1] = (positions[i*3+1]-minY) + dy * defScale;
      deformedPositions[i*3+2] = (positions[i*3+2]-minZ) + dz * defScale;
      const mag = getDispMagnitude(dx, dy, dz);
      const t = maxMag > 0 ? mag / maxMag : 0;
      const [r, g, b] = jetColor(t);
      colors[i*3]=r; colors[i*3+1]=g; colors[i*3+2]=b;
    }

    const defGeo = new THREE.BufferGeometry();
    defGeo.setAttribute('position', new THREE.BufferAttribute(deformedPositions, 3));
    defGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    defGeo.computeVertexNormals();
    const defMat = new THREE.MeshPhongMaterial({ vertexColors: true, side: THREE.DoubleSide, shininess: 40 });
    const defMesh = new THREE.Mesh(defGeo, defMat);
    scene.add(defMesh);

    if (showOriginal) {
      const origPositions = new Float32Array(positions.length);
      for (let i = 0; i < nv; i++) {
        origPositions[i*3]   = positions[i*3]-minX;
        origPositions[i*3+1] = positions[i*3+1]-minY;
        origPositions[i*3+2] = positions[i*3+2]-minZ;
      }
      const origGeo = new THREE.BufferGeometry();
      origGeo.setAttribute('position', new THREE.BufferAttribute(origPositions, 3));
      origGeo.computeVertexNormals();
      const origMat = new THREE.MeshBasicMaterial({ color: 0x1e1e28, wireframe: true, opacity: 0.3, transparent: true });
      scene.add(new THREE.Mesh(origGeo, origMat));
    }

    const lookTarget = new THREE.Vector3(centreX, centreY, centreZ);
    camera.up.set(0, 0, 1);
    camera.position.set(centreX + span * 1.5, centreY + span * 1.2, centreZ + span);
    camera.lookAt(lookTarget);

    scene.add(new THREE.AxesHelper(span * 0.4));

    const arrowGroup = new THREE.Group();
    scene.add(arrowGroup);

    let isDragging = false, lastX = 0, lastY = 0;
    let rotX = 0.55, rotY = 0.8, zoom = 1;

    const onMouseDown = (e) => { isDragging = true; lastX = e.clientX; lastY = e.clientY; };
    const onMouseMove = (e) => {
      if (!isDragging) return;
      const dx = (e.clientX - lastX) * 0.005, dy = (e.clientY - lastY) * 0.005;
      rotY += dx; rotX += dy; lastX = e.clientX; lastY = e.clientY;
    };
    const onMouseUp = () => { isDragging = false; };
    const onWheel = (e) => { zoom *= e.deltaY > 0 ? 1.1 : 0.91; };
    renderer.domElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: true });

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      camera.position.set(
        centreX + span * 1.8 * zoom * Math.cos(rotX) * Math.cos(rotY),
        centreY + span * 1.8 * zoom * Math.cos(rotX) * Math.sin(rotY),
        centreZ + span * 1.8 * zoom * Math.sin(rotX)
      );
      camera.lookAt(lookTarget);
      renderer.render(scene, camera);
    };
    animate();

    sceneRef.current = { scene, arrowGroup, span, spanX, spanY, spanZ };

    return () => {
      unbindBg();
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.forceContextLoss();
      renderer.dispose();
      sceneRef.current = null;
    };
  }, [positions, strainTensor, maxDisp, showOriginal, defScale]);

  useEffect(() => {
    const s = sceneRef.current;
    if (!s || !orient) return;
    while (s.arrowGroup.children.length) s.arrowGroup.remove(s.arrowGroup.children[0]);
    const buildDir = getBuildDirectionVector(orient.rx, orient.ry, orient.rz);
    createBuildArrow(s.arrowGroup, s.span, buildDir, { spanX: s.spanX, spanY: s.spanY, spanZ: s.spanZ });
  }, [orient]);

  return <div ref={mountRef} className="cte-viewer-wrap" />;
}

// ─── COLORBAR ─────────────────────────────────────────────────────────────────
export function ColorBar({ maxVal, unit = 'mm' }) {
  const stops = Array.from({ length: 20 }, (_, i) => {
    const t = i / 19;
    const [r, g, b] = jetColor(t);
    return `rgb(${(r*255)|0},${(g*255)|0},${(b*255)|0})`;
  });
  return (
    <div className="cte-colorbar">
      <span className="cte-colorbar-label">0 {unit}</span>
      <div className="cte-colorbar-gradient" style={{ background: `linear-gradient(to right, ${stops.join(',')})` }} />
      <span className="cte-colorbar-label">{maxVal.toFixed(3)} {unit}</span>
      <div className="cte-colorbar-legend">
        {[['X', 'var(--rf-info)'], ['Y', 'var(--rf-primary)'], ['Z', 'var(--rf-success)']].map(([ax, col]) => (
          <span key={ax} style={{ fontSize: 'var(--rf-font-size-xs)', color: col, fontWeight: 600 }}>{ax}</span>
        ))}
        <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', fontWeight: 600 }}>BUILD</span>
      </div>
    </div>
  );
}

// ─── BUILD ANGLE GIZMO (3D Build Plate with Ring Handles) ────────────────────
export function BuildAngleGizmo({ buildAngle, buildAzimuth, onAngleChange, size }) {
  const mountRef = useRef(null);
  const sceneDataRef = useRef(null);
  const valuesRef = useRef({ buildAngle, buildAzimuth, onAngleChange, size });

  useEffect(() => {
    valuesRef.current = { buildAngle, buildAzimuth, onAngleChange, size };
  });

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const W = mount.clientWidth || 400;
    const H = 300;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    const unbindBg = bindViewerBackground(renderer, 0x0d0d14);
    mount.innerHTML = '';
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 100);
    camera.up.set(0, 0, 1);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dL = new THREE.DirectionalLight(0xffffff, 0.7);
    dL.position.set(2, 3, 4);
    scene.add(dL);

    const grid = new THREE.GridHelper(4.4, 8, 0x333355, 0x1a1a2e);
    grid.rotation.x = Math.PI / 2;
    scene.add(grid);
    scene.add(new THREE.AxesHelper(1.15));

    const azimuthGroup = new THREE.Group();
    scene.add(azimuthGroup);

    const tiltGroup = new THREE.Group();
    azimuthGroup.add(tiltGroup);

    const slabMat = new THREE.MeshPhongMaterial({
      color: 0xa74e9e, transparent: true, opacity: 0.45,
      shininess: 30, side: THREE.DoubleSide,
    });
    const slab = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), slabMat);
    tiltGroup.add(slab);
    const edgeMat = new THREE.LineBasicMaterial({ color: 0xc87bbe, opacity: 0.6, transparent: true });
    const layerMat = new THREE.LineBasicMaterial({ color: 0xc87bbe, opacity: 0.25, transparent: true });

    const arrow = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, 0),
      0.8, 0xd946ef, 0.14, 0.08
    );
    tiltGroup.add(arrow);

    const segs = 48;
    const ringMat = (color) => new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5 });
    const greenMat = ringMat(0x22c55e);
    const redMat = ringMat(0xef4444);
    const blueMat = ringMat(0x3b82f6);
    const greenRing = new THREE.Mesh(new THREE.TorusGeometry(1.3, 0.045, 8, segs), greenMat);
    greenRing.userData.handle = 'rotation';
    scene.add(greenRing);
    const redRing = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.045, 8, segs), redMat);
    redRing.rotation.y = Math.PI / 2;
    redRing.userData.handle = 'tilt';
    azimuthGroup.add(redRing);
    const blueRing = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.045, 8, segs), blueMat);
    blueRing.rotation.x = Math.PI / 2;
    blueRing.userData.handle = 'tilt';
    azimuthGroup.add(blueRing);

    const hitMat = () => new THREE.MeshBasicMaterial({ visible: false });
    const greenHit = new THREE.Mesh(new THREE.TorusGeometry(1.3, 0.16, 8, segs), hitMat());
    scene.add(greenHit);
    const redHit = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.16, 8, segs), hitMat());
    redHit.rotation.y = Math.PI / 2;
    azimuthGroup.add(redHit);
    const blueHit = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.16, 8, segs), hitMat());
    blueHit.rotation.x = Math.PI / 2;
    azimuthGroup.add(blueHit);

    const applyPlate = (plateSize) => {
      const { sx, sy, sz } = layoutPlate(plateSize);
      slab.geometry.dispose();
      slab.geometry = new THREE.BoxGeometry(sx, sy, sz);
      slab.position.set(0, 0, sz / 2);

      const prevEdges = slab.getObjectByName('plate-edges');
      if (prevEdges) { slab.remove(prevEdges); prevEdges.geometry.dispose(); }
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(slab.geometry), edgeMat);
      edges.name = 'plate-edges';
      slab.add(edges);

      const prevLayers = slab.getObjectByName('plate-layers');
      if (prevLayers) { slab.remove(prevLayers); prevLayers.geometry.dispose(); }
      const longest = Math.max(sx, sy, sz);
      if (sz > longest * 0.08) {
        const layerVerts = [];
        const lc = 6;
        const o = 0.004;
        for (let i = 1; i < lc; i++) {
          const z = -sz / 2 + (i / lc) * sz;
          layerVerts.push(-sx / 2, sy / 2 + o, z, sx / 2, sy / 2 + o, z);
          layerVerts.push(-sx / 2, -sy / 2 - o, z, sx / 2, -sy / 2 - o, z);
          layerVerts.push(-sx / 2 - o, -sy / 2, z, -sx / 2 - o, sy / 2, z);
          layerVerts.push(sx / 2 + o, -sy / 2, z, sx / 2 + o, sy / 2, z);
        }
        const lGeo = new THREE.BufferGeometry();
        lGeo.setAttribute('position', new THREE.Float32BufferAttribute(layerVerts, 3));
        const layers = new THREE.LineSegments(lGeo, layerMat);
        layers.name = 'plate-layers';
        slab.add(layers);
      }

      const reach = 0.5 * Math.hypot(sx, sy);
      const spinR = Math.max(1.15, reach * 1.2);
      const tiltR = spinR * 0.86;
      const tube = Math.max(0.03, spinR * 0.034);
      const fit = (mesh, radius, tubeRadius) => {
        mesh.geometry.dispose();
        mesh.geometry = new THREE.TorusGeometry(radius, tubeRadius, 8, segs);
        mesh.position.set(0, 0, sz / 2);
      };
      fit(greenRing, spinR, tube);
      fit(greenHit, spinR, tube * 3.2);
      fit(redRing, tiltR, tube);
      fit(redHit, tiltR, tube * 3.2);
      fit(blueRing, tiltR, tube);
      fit(blueHit, tiltR, tube * 3.2);

      const arrowLen = Math.max(0.55, Math.min(1.25, Math.max(sz * 2.5, 0.55)));
      arrow.position.set(0, 0, sz);
      arrow.setDirection(new THREE.Vector3(0, 0, 1));
      arrow.setLength(arrowLen, arrowLen * 0.18, arrowLen * 0.1);
      if (sceneDataRef.current) sceneDataRef.current.lookZ = sz / 2;
    };

    const hitRings = [greenHit, redHit, blueHit];
    const hitToVisible = new Map([
      [greenHit, greenRing],
      [redHit, redRing],
      [blueHit, blueRing],
    ]);

    const allRings = [greenRing, redRing, blueRing];
    const allMats = [greenMat, redMat, blueMat];
    const baseColors = [0x22c55e, 0xef4444, 0x3b82f6];
    const hoverColors = [0x4ade80, 0xf87171, 0x60a5fa];

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const orbit = { theta: 0.9, phi: 0.62, radius: 6 };
    sceneDataRef.current = {
      renderer, scene, camera, azimuthGroup, tiltGroup, orbit,
      raycaster, pointer, W, H, applyPlate, lookZ: 0.1,
    };
    applyPlate(valuesRef.current.size);

    let mode = null;
    let lastX = 0, lastY = 0;
    let hoveredRing = null;
    let isOverCanvas = false;
    let activeRing = null;
    let dragCenterX = 0, dragCenterY = 0;
    let dragLastMx = 0, dragLastMy = 0;
    let dragSign = 1;

    const toScreen = (wx, wy, wz) => {
      const v = new THREE.Vector3(wx, wy, wz).project(camera);
      return { x: (v.x + 1) / 2 * W, y: (-v.y + 1) / 2 * H };
    };

    const getRingWorldAxis = (ring) => {
      const azRad = valuesRef.current.buildAzimuth * Math.PI / 180;
      if (ring === greenRing) return new THREE.Vector3(0, 0, 1);
      if (ring === redRing) return new THREE.Vector3(Math.cos(azRad), Math.sin(azRad), 0);
      return new THREE.Vector3(-Math.sin(azRad), Math.cos(azRad), 0);
    };

    const getRingHit = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / W) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / H) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(hitRings);
      return hits.length > 0 ? hitToVisible.get(hits[0].object) : null;
    };

    const setHover = (ring) => {
      if (hoveredRing === ring) return;
      hoveredRing = ring;
      for (let i = 0; i < allRings.length; i++) {
        allMats[i].opacity = allRings[i] === ring ? 0.9 : 0.4;
        allMats[i].color.setHex(allRings[i] === ring ? hoverColors[i] : baseColors[i]);
      }
      renderer.domElement.style.cursor = ring ? 'grab' : 'default';
    };

    const onPointerDown = (e) => {
      e.preventDefault();
      lastX = e.clientX;
      lastY = e.clientY;

      if (e.button === 2 || e.ctrlKey || e.metaKey) {
        mode = 'orbit';
        return;
      }

      const ring = getRingHit(e);
      if (ring) {
        mode = ring.userData.handle;
        activeRing = ring;
        renderer.domElement.setPointerCapture(e.pointerId);
        renderer.domElement.style.cursor = 'grabbing';
        const rect = renderer.domElement.getBoundingClientRect();
        const mid = sceneDataRef.current.lookZ || 0;
        const center = toScreen(0, 0, mid);
        dragCenterX = center.x;
        dragCenterY = center.y;
        dragLastMx = e.clientX - rect.left;
        dragLastMy = e.clientY - rect.top;

        const ringAxis = getRingWorldAxis(ring);
        const camDir = new THREE.Vector3()
          .subVectors(camera.position, new THREE.Vector3(0, 0, mid))
          .normalize();
        dragSign = camDir.dot(ringAxis) > 0 ? -1 : 1;
      } else {
        mode = 'orbit';
      }
    };

    const onPointerMove = (e) => {
      if (!mode) {
        if (!isOverCanvas) return;
        setHover(getRingHit(e));
        return;
      }

      if (mode === 'orbit') {
        const dx = e.clientX - lastX;
        const dy = e.clientY - lastY;
        lastX = e.clientX;
        lastY = e.clientY;
        orbit.theta -= dx * 0.005;
        orbit.phi = Math.max(0.1, Math.min(1.4, orbit.phi + dy * 0.005));
        return;
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      const v1x = dragLastMx - dragCenterX, v1y = dragLastMy - dragCenterY;
      const v2x = mx - dragCenterX, v2y = my - dragCenterY;

      const dist = Math.sqrt(v2x * v2x + v2y * v2y);
      if (dist < 10) return;

      const cross = v1x * v2y - v1y * v2x;
      const dot = v1x * v2x + v1y * v2y;
      const angleDelta = Math.atan2(cross, dot);

      dragLastMx = mx;
      dragLastMy = my;

      if (Math.abs(angleDelta) < 0.0001) return;

      const deltaDeg = angleDelta * 180 / Math.PI * dragSign;

      const val = valuesRef.current;
      if (mode === 'rotation') {
        val.onAngleChange(val.buildAngle, ((val.buildAzimuth + deltaDeg) % 360 + 360) % 360);
      } else if (mode === 'tilt') {
        val.onAngleChange(Math.max(0, Math.min(90, val.buildAngle + deltaDeg)), val.buildAzimuth);
      }
    };

    const onPointerUp = (e) => {
      if (activeRing) {
        renderer.domElement.releasePointerCapture(e.pointerId);
        const val = valuesRef.current;
        val.onAngleChange(Math.round(val.buildAngle), Math.round(val.buildAzimuth));
      }
      mode = null;
      activeRing = null;
      setHover(hoveredRing);
    };

    const onEnter = () => { isOverCanvas = true; };
    const onLeave = () => { isOverCanvas = false; if (!mode) setHover(null); };

    const onWheel = (e) => {
      orbit.radius = Math.max(3, Math.min(10, orbit.radius + e.deltaY * 0.005));
    };
    const onCtx = (e) => e.preventDefault();

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    renderer.domElement.addEventListener('pointerenter', onEnter);
    renderer.domElement.addEventListener('pointerleave', onLeave);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: true });
    renderer.domElement.addEventListener('contextmenu', onCtx);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const d = sceneDataRef.current;
      if (!d) return;
      const lookZ = d.lookZ || 0;
      d.camera.position.set(
        d.orbit.radius * Math.cos(d.orbit.phi) * Math.cos(d.orbit.theta),
        d.orbit.radius * Math.cos(d.orbit.phi) * Math.sin(d.orbit.theta),
        lookZ + d.orbit.radius * Math.sin(d.orbit.phi)
      );
      d.camera.lookAt(0, 0, lookZ);
      d.renderer.render(d.scene, d.camera);
    };
    animate();

    return () => {
      unbindBg();
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      renderer.domElement.removeEventListener('pointerenter', onEnter);
      renderer.domElement.removeEventListener('pointerleave', onLeave);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('contextmenu', onCtx);
      renderer.forceContextLoss();
      renderer.dispose();
      sceneDataRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const d = sceneDataRef.current;
    if (!d) return;
    d.azimuthGroup.rotation.z = buildAzimuth * Math.PI / 180;
    d.tiltGroup.rotation.y = buildAngle * Math.PI / 180;
  }, [buildAngle, buildAzimuth]);

  useEffect(() => {
    const d = sceneDataRef.current;
    if (!d || !d.applyPlate) return;
    d.applyPlate(size);
  }, [size && size.x, size && size.y, size && size.z]);

  const presets = [
    { label: 'Flat', angle: 0, az: 0 },
    { label: '15\u00B0', angle: 15 },
    { label: '30\u00B0', angle: 30 },
    { label: '45\u00B0', angle: 45 },
    { label: '60\u00B0', angle: 60 },
    { label: 'On Side', angle: 90 },
  ];

  return (
    <div className="cte-gizmo-3d">
      <div className="cte-gizmo-viewport-wrap">
        <div ref={mountRef} className="cte-gizmo-viewport" />
        <div className="cte-gizmo-legend">
          <span style={{ color: '#ef4444' }}>BUILD ANGLE</span>
          <span style={{ color: '#22c55e' }}>ROTATION</span>
        </div>
      </div>
      <div style={{ padding: '6px 12px 0', fontSize: 12, color: 'var(--rf-text-secondary)' }}>
        {size ? 'Drawn to your X, Y and Z. A flat print grows in Z.' : 'Example plate. A flat print grows in Z.'}
      </div>
      <div className="cte-gizmo-presets">
        {presets.map(p => (
          <button
            key={p.label}
            className={`cte-gizmo-preset ${buildAngle === p.angle ? 'active' : ''}`}
            onClick={() => onAngleChange(p.angle, p.az !== undefined ? p.az : buildAzimuth)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="cte-gizmo-bar">
        <div className="cte-gizmo-inputs">
          <label>
            <span style={{ color: '#ef4444' }}>BUILD</span>
            <input type="number" min={0} max={90} value={buildAngle}
              onChange={e => onAngleChange(Math.max(0, Math.min(90, +e.target.value || 0)), buildAzimuth)} />
            <span>deg</span>
          </label>
          <label>
            <span style={{ color: '#22c55e' }}>ROTATION</span>
            <input type="number" min={0} max={359} value={buildAzimuth}
              onChange={e => onAngleChange(buildAngle, Math.max(0, Math.min(359, +e.target.value || 0)))} />
            <span>deg</span>
          </label>
        </div>
        <span className="cte-gizmo-hint">
          Grab rings to adjust | Right-drag to orbit | Scroll to zoom
        </span>
      </div>
    </div>
  );
}

// ─── ANIMATED CYCLE VIEWER ────────────────────────────────────────────────────
export function CycleSTLViewer({ positions, frames, frameIndex, globalMaxDisp, showOriginal, orient, defScale = 1 }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current || !positions || !frames || !frames.length) return;
    const W = mountRef.current.clientWidth || 700;
    const H = mountRef.current.clientHeight || 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    const unbindBg = bindViewerBackground(renderer, 0x0a0a0f);
    mountRef.current.innerHTML = '';
    mountRef.current.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.001, 100000);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(1, 2, 1.5);
    scene.add(dirLight);

    const nv = positions.length / 3;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < nv; i++) {
      const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    }
    const spanX = maxX - minX, spanY = maxY - minY, spanZ = maxZ - minZ;
    const span = Math.max(spanX, spanY, spanZ);
    const centreX = spanX / 2, centreY = spanY / 2, centreZ = spanZ / 2;
    const base = computeBaseRef(positions);
    const bx = base.x, by = base.y, bz = base.z;

    const deformedPositions = new Float32Array(positions.length);
    const colors = new Float32Array(nv * 3);
    for (let i = 0; i < nv; i++) {
      deformedPositions[i * 3] = positions[i * 3] - minX;
      deformedPositions[i * 3 + 1] = positions[i * 3 + 1] - minY;
      deformedPositions[i * 3 + 2] = positions[i * 3 + 2] - minZ;
      colors[i * 3] = 0; colors[i * 3 + 1] = 0; colors[i * 3 + 2] = 0.5;
    }

    const defGeo = new THREE.BufferGeometry();
    defGeo.setAttribute('position', new THREE.BufferAttribute(deformedPositions, 3));
    defGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    defGeo.computeVertexNormals();
    const defMat = new THREE.MeshPhongMaterial({ vertexColors: true, side: THREE.DoubleSide, shininess: 40 });
    const defMesh = new THREE.Mesh(defGeo, defMat);
    scene.add(defMesh);

    if (showOriginal) {
      const origPositions = new Float32Array(positions.length);
      for (let i = 0; i < nv; i++) {
        origPositions[i * 3] = positions[i * 3] - minX;
        origPositions[i * 3 + 1] = positions[i * 3 + 1] - minY;
        origPositions[i * 3 + 2] = positions[i * 3 + 2] - minZ;
      }
      const origGeo = new THREE.BufferGeometry();
      origGeo.setAttribute('position', new THREE.BufferAttribute(origPositions, 3));
      origGeo.computeVertexNormals();
      const origMat = new THREE.MeshBasicMaterial({ color: 0x1e1e28, wireframe: true, opacity: 0.3, transparent: true });
      scene.add(new THREE.Mesh(origGeo, origMat));
    }

    scene.add(new THREE.AxesHelper(span * 0.4));

    if (orient) {
      const buildDir = getBuildDirectionVector(orient.rx, orient.ry, orient.rz);
      createBuildArrow(scene, span, buildDir, { spanX, spanY, spanZ });
    }

    const lookTarget = new THREE.Vector3(centreX, centreY, centreZ);
    camera.up.set(0, 0, 1);
    camera.position.set(centreX + span * 1.5, centreY + span * 1.2, centreZ + span);
    camera.lookAt(lookTarget);

    let isDragging = false, lastMouseX = 0, lastMouseY = 0;
    let rotX = 0.55, rotY = 0.8, zoom = 1;
    const onMouseDown = (e) => { isDragging = true; lastMouseX = e.clientX; lastMouseY = e.clientY; };
    const onMouseMove = (e) => {
      if (!isDragging) return;
      rotY += (e.clientX - lastMouseX) * 0.005;
      rotX += (e.clientY - lastMouseY) * 0.005;
      lastMouseX = e.clientX; lastMouseY = e.clientY;
    };
    const onMouseUp = () => { isDragging = false; };
    const onWheel = (e) => { zoom *= e.deltaY > 0 ? 1.1 : 0.91; };
    renderer.domElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: true });

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      camera.position.set(
        centreX + span * 1.8 * zoom * Math.cos(rotX) * Math.cos(rotY),
        centreY + span * 1.8 * zoom * Math.cos(rotX) * Math.sin(rotY),
        centreZ + span * 1.8 * zoom * Math.sin(rotX)
      );
      camera.lookAt(lookTarget);
      renderer.render(scene, camera);
    };
    animate();

    sceneRef.current = { renderer, animId, defGeo, nv, minX, minY, minZ, bx, by, bz, span };

    return () => {
      unbindBg();
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.forceContextLoss();
      renderer.dispose();
      sceneRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, showOriginal]);

  useEffect(() => {
    if (!sceneRef.current || !frames || !frames.length) return;
    const { defGeo, nv, minX, minY, minZ, bx, by, bz } = sceneRef.current;
    const frame = frames[Math.min(frameIndex, frames.length - 1)];
    const tensor = frame.tensor;
    const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = tensor.flat();
    const maxMag = globalMaxDisp || 1e-9;

    const posAttr = defGeo.getAttribute('position');
    const colAttr = defGeo.getAttribute('color');
    for (let i = 0; i < nv; i++) {
      const rx = positions[i * 3] - bx, ry = positions[i * 3 + 1] - by, rz = positions[i * 3 + 2] - bz;
      const dx = e00 * rx + e01 * ry + e02 * rz;
      const dy = e10 * rx + e11 * ry + e12 * rz;
      const dz = e20 * rx + e21 * ry + e22 * rz;
      posAttr.array[i * 3] = (positions[i * 3] - minX) + dx * defScale;
      posAttr.array[i * 3 + 1] = (positions[i * 3 + 1] - minY) + dy * defScale;
      posAttr.array[i * 3 + 2] = (positions[i * 3 + 2] - minZ) + dz * defScale;
      const mag = getDispMagnitude(dx, dy, dz);
      const t = Math.min(1, mag / maxMag);
      const [r, g, b] = jetColor(t);
      colAttr.array[i * 3] = r;
      colAttr.array[i * 3 + 1] = g;
      colAttr.array[i * 3 + 2] = b;
    }
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    defGeo.computeVertexNormals();
  }, [frameIndex, frames, positions, globalMaxDisp, defScale]);

  return <div ref={mountRef} className="cte-viewer-wrap" />;
}
